import axios from 'axios';

import { ReportSchedulePayload } from 'src/features/reports/components/schedule-report-form/types';

import {
  api,
  READINGS,
  PUMP_REPORT,
  REPORTS,
  API_V2,
  CUSTOMERS,
  TEMPLATES,
  API_V3,
  PDF_REPORTS,
} from '../api';
import { GetAllByRangeParams, GetAllTemplates, TemplateImageKind } from './types';
import { returnReportType } from './getReportType';

const getAll = async (customerId: string, startDate: string, endDdate: string) => {
  try {
    const response = await api({
      method: 'get',
      url: `v2/customers/${customerId}/reports?customerId=${customerId}&startDate=${startDate}&endDate=${endDdate}&reportType=pump_report`,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

const scheduleReport = async (
  payload: ReportSchedulePayload,
  templateId: string,
  customerId: string,
  scheduleId: string | null
) => {
  let response;
  if (scheduleId) {
    response = await api.put(
      `${API_V2}/${CUSTOMERS}/${customerId}/schedules/${templateId}/${scheduleId}`,
      payload
    );
  } else {
    response = await api.post(
      `${API_V2}/${CUSTOMERS}/${customerId}/schedules/${templateId}`,
      payload
    );
  }

  return response.data;
};
const getScheduledReport = async (templateId: string, customerId: string) => {
  const response = await api.get<ReportSchedulePayload & { id: string }>(
    `${API_V2}/${CUSTOMERS}/${customerId}/schedules/${templateId}`
  );

  return response.data;
};

const getAllTemplates = async ({ customerId, createdBy, isFavorite, search }: GetAllTemplates) => {
  const queryParams = new URLSearchParams();
  if (createdBy) queryParams.append('createdBy', createdBy);
  if (isFavorite) queryParams.append('isFavorite', String(isFavorite));
  if (search) queryParams.append('search', search);

  try {
    const response = await api({
      method: 'get',
      url: `${API_V2}/${CUSTOMERS}/${customerId}/${TEMPLATES}?${queryParams.toString()}`,
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

const getHistoryTemplates = async (customerId: string, templateId: string, reportType: string) => {
  try {
    const reponse = await api({
      method: 'get',
      url: `${API_V2}/${CUSTOMERS}/${customerId}/${TEMPLATES}/${returnReportType(
        reportType
      )}/${templateId}/${REPORTS}`,
    });

    return reponse.data;
  } catch (error) {
    console.error(error);
  }
};

const getDataHistoryTemplateByInstanceId = async (customerId: string, reportId: string) => {
  try {
    const response = await api({
      method: 'get',
      url: `${API_V2}/${CUSTOMERS}/${customerId}/${REPORTS}/data-reports/${reportId}`,
    });

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

const getAssetHistoryTemplateByInstanceId = async (customerId: string, reportId: string) => {
  try {
    const response = await api({
      method: 'get',
      url: `${API_V2}/${CUSTOMERS}/${customerId}/${REPORTS}/asset-reports/${reportId}`,
    });

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

const updateTemplateById = async (templateId: string, customerId: string, data: any) => {
  try {
    const response = await api({
      method: 'patch',
      url: `${API_V2}/${CUSTOMERS}/${customerId}/${TEMPLATES}/asset-reports/${templateId}`,
      data,
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

const exportAssetReport = async ({
  customerId,
  data,
  format,
  templateId,
}: {
  customerId: string;
  data?: any;
  format: 'excel' | 'csv';
  templateId?: string | null;
}) => {
  const mimeType =
    format === 'excel'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'text/csv';

  const response = await api({
    method: 'POST',
    url: `${API_V2}/${CUSTOMERS}/${customerId}/${REPORTS}/asset-reports/export-request${
      templateId ? `?templateId=${templateId}` : ''
    }`,
    responseType: 'blob',
    headers: {
      'media-type': mimeType,
    },
    data: data,
  });

  const contentDisposition = response.headers['Content-Disposition'];

  let fileName = 'download'; // Default name if not found
  if (contentDisposition) {
    const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    if (fileNameMatch && fileNameMatch[1]) {
      fileName = fileNameMatch[1];
    }
  }

  const blob = new Blob([response.data], { type: mimeType });

  // Step 2: Create a download link
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName; // Set the file name for the download

  // Step 3: Programmatically click the link to trigger the download
  document.body.appendChild(link);
  link.click();

  // Step 4: Clean up and remove the link
  document.body.removeChild(link);
  return response.data;
};

const exportDataReport = async ({
  customerId,
  data,
  format,
  reportId,
}: {
  customerId: string;
  data?: any;
  format: 'excel' | 'csv';
  reportId?: string | null;
}) => {
  const mimeType =
    format === 'excel'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'text/csv';

  const response = await api({
    method: 'POST',
    url: `${API_V2}/${CUSTOMERS}/${customerId}/${REPORTS}/data-reports/export-request`,
    responseType: 'blob',
    headers: {
      'media-type': mimeType,
    },
    data: {
      dataReportTemplate: data,
      reportId: reportId,
    },
  });

  const contentDisposition = response.headers['Content-Disposition'];

  let fileName = 'download'; // Default name if not found
  if (contentDisposition) {
    const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    if (fileNameMatch && fileNameMatch[1]) {
      fileName = fileNameMatch[1];
    }
  }

  const blob = new Blob([response.data], { type: mimeType });

  // Step 2: Create a download link
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName; // Set the file name for the download

  // Step 3: Programmatically click the link to trigger the download
  document.body.appendChild(link);
  link.click();

  // Step 4: Clean up and remove the link
  document.body.removeChild(link);
  return response.data;
};

const getCount = async (customerId: string) => {
  try {
    const response = await api({
      method: 'get',
      url: `${API_V2}/${CUSTOMERS}/${customerId}/${TEMPLATES}/count`,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

const saveReportTemplate = async (customerId: string, data: any) => {
  let payload = data;
  if (data.reportType === 'PDF' || data.reportType === 'PDF_SITE_STATUS') {
    payload = {
      reportTemplate: data.reportTemplate,
      template: data,
    };
  }
  const response = await api({
    method: 'POST',
    url: `v2/customers/${customerId}/templates/${returnReportType(data.reportType)}`,
    data: payload,
  });
  return response.data;
};

const deleteTemplate = async (customerId: string, templateId: string, reportType: string) => {
  const response = await api({
    method: 'DELETE',
    url: `v2/${CUSTOMERS}/${customerId}/${TEMPLATES}/${returnReportType(reportType)}/${templateId}`,
  });
  return response.data;
};
const saveDataReport = async (customerId: string, data: { dataReportTemplateId: string }) => {
  const response = await api({
    method: 'post',
    url: `${API_V2}/${CUSTOMERS}/${customerId}/${REPORTS}/data-reports`,
    data,
  });
  return response.data;
};

const saveAssetReport = async (
  customerId: string,
  data: { templateId: string; reportId: string }
) => {
  const response = await api({
    method: 'post',
    url: `${API_V2}/${CUSTOMERS}/${customerId}/${REPORTS}/asset-reports`,
    data,
  });
  return response.data;
};

const savePDFReport = async (
  customerId: string,
  data: { templateId: string; reportId: string }
) => {
  const response = await api({
    method: 'post',
    url: `${API_V2}/${CUSTOMERS}/${customerId}/${REPORTS}/pdf-reports`,
    data,
  });
  return response.data;
};
const updateReportTemplate = async (templateId: string, customerId: string, data: any) => {
  let payload = data;
  if (data.reportType === 'PDF' || data.reportType === 'PDF_SITE_STATUS') {
    payload = {
      reportTemplate: data.reportTemplate,
      template: data,
    };
  }
  const response = await api({
    method: 'PATCH',
    url: `v2/customers/${customerId}/templates/${returnReportType(data.reportType)}/${templateId}`,
    data: payload,
  });
  return response.data;
};
const getReportTemplateById = async (
  reportType: string,
  customerId: string,
  templateId: string
) => {
  const response = await api({
    method: 'GET',
    url: `v2/${CUSTOMERS}/${customerId}/${TEMPLATES}/${returnReportType(reportType)}/${templateId}`,
  });
  return response.data;
};

const previewReport = async (
  customerId: string,
  data: any,
  controller: AbortController,
  reportType: string
) => {
  const response = await api({
    method: 'POST',
    signal: controller.signal,
    url: `v2/customers/${customerId}/reports/${returnReportType(reportType)}/generator`,
    data: reportType === 'DATA' ? { dataReportTemplate: data } : data,
  });
  return response.data;
};

const getAllByRange = async (params: GetAllByRangeParams) => {
  try {
    const response = await api({
      method: 'get',
      url: `${READINGS}/${params.customerId}${PUMP_REPORT}/between?fromDate=${params.fromDate}&toDate=${params.toDate}&limit=${params.limit}`,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

// V3 REPORTS ENDPOINTS
const generateReport = async (
  customerId: string,
  data: { templateId: string; reportType: string }
) => {
  const response = await api({
    method: 'POST',
    url: `${API_V3}/${CUSTOMERS}/${customerId}/${REPORTS}`,
    data: data,
  });
  return response.data;
};

const cancelReport = async (customerId: string, templateId: string, reportId: string) => {
  const response = await api({
    method: 'POST',
    url: `${API_V3}/${CUSTOMERS}/${customerId}/${REPORTS}/${reportId}/cancel?templateId=${templateId}`,
  });
  return response;
};

const getReportMetadata = async (
  customerId: string,
  templateId: string,
  limit?: number,
  lastEvaluatedKey?: string
) => {
  try {
    const response = await api({
      method: 'get',
      url: `${API_V3}/${CUSTOMERS}/${customerId}/${REPORTS}?limit=${limit}&lastEvaluatedKey=${lastEvaluatedKey}&templateId=${templateId}`,
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

const retryReportGeneration = async (customerId: string, templateId: string, reportId: string) => {
  const response = await api({
    method: 'POST',
    url: `${API_V3}/${CUSTOMERS}/${customerId}/${REPORTS}/${reportId}/retry?templateId=${templateId}`,
  });
  return response;
};

const getReportInline = async (customerId: string, templateId: string, reportId: string) => {
  try {
    const response = await api({
      method: 'GET',
      url: `${API_V3}/${CUSTOMERS}/${customerId}/${REPORTS}/${reportId}?templateId=${templateId}`,
    });
    return response;
  } catch (error) {
    console.error(error);
    return error;
  }
};

const getReportDownloadUrl = async (
  customerId: string,
  templateId: string,
  reportId: string,
  exportType: string
) => {
  try {
    const response = await api({
      method: 'GET',
      url: `${API_V3}/${CUSTOMERS}/${customerId}/${REPORTS}/${reportId}/download-url?templateId=${templateId}&exportType=${exportType}`,
    });
    return response;
  } catch (error) {
    console.error(error);
  }
};

// Not api because this is a full download URL already given
const downloadCSVReport = async (reportDownloadURL: string) => {
  try {
    const response = await axios.get(reportDownloadURL);
    return response;
  } catch (error) {
    console.error(error);
  }
};

const deleteReportV3 = async (customerId: string, reportId: string, templateId: string) => {
  const response = await api({
    method: 'DELETE',
    url: `${API_V3}/${CUSTOMERS}/${customerId}/${REPORTS}/${reportId}?templateId=${templateId}`,
  });
  return response.data;
};

const getAiSummary = async (customerId: string, reportId: string, templateId: string) => {
  const response = await api({
    method: 'GET',
    url: `${API_V3}/${CUSTOMERS}/${customerId}/${REPORTS}/${reportId}/ai-summary?templateId=${templateId}`,
  });
  return response.data;
};

// The tag set the upload-init PUT is signed with; S3 rejects the PUT unless it is sent as is.
const TEMPLATE_IMAGE_UPLOAD_TAGGING = 'status=temp';

const templateImageUploadsUrl = (customerId: string) =>
  `${API_V2}/${CUSTOMERS}/${customerId}/${TEMPLATES}/${PDF_REPORTS}/uploads`;

// Presign, PUT straight to S3, then finalize; resolves to the normalised key for the next Save.
// contentType must be the canonical type: the PUT is signed with exactly that value.
const uploadTemplateImage = async (
  customerId: string,
  kind: TemplateImageKind,
  file: File,
  contentType: string
) => {
  const { data: upload } = await api.post<{ url: string; key: string }>(
    templateImageUploadsUrl(customerId),
    { kind, contentType }
  );

  // Not api: the presigned URL is absolute and must not carry our auth headers
  const response = await fetch(upload.url, {
    method: 'PUT',
    headers: { 'Content-Type': contentType, 'x-amz-tagging': TEMPLATE_IMAGE_UPLOAD_TAGGING },
    body: file,
  });
  if (!response.ok) throw new Error(`S3 upload failed with status ${response.status}`);

  const { data: finalized } = await api.post<{ key: string }>(
    `${templateImageUploadsUrl(customerId)}/finalize`,
    { kind, key: upload.key }
  );
  return finalized.key;
};

export const ReportsService = {
  generateReport,
  getAll,
  getCount,
  getAllTemplates,
  getHistoryTemplates,
  getReportMetadata,
  getDataHistoryTemplateByInstanceId,
  getAssetHistoryTemplateByInstanceId,
  getAllByRange,
  saveReportTemplate,
  saveDataReport,
  saveAssetReport,
  savePDFReport,
  previewReport,
  updateTemplateById,
  deleteTemplate,
  getScheduledReport,
  updateReportTemplate,
  exportDataReport,
  scheduleReport,
  exportAssetReport,
  getReportTemplateById,
  cancelReport,
  retryReportGeneration,
  getReportInline,
  getReportDownloadUrl,
  downloadCSVReport,
  deleteReportV3,
  getAiSummary,
  uploadTemplateImage,
};
